import { makeAutoObservable, runInAction } from 'mobx'
import { debounce } from 'lodash'
import { injectable, inject, decorate } from '@evertel/di'
import { DocumentStore, DocumentACLStore, DocumentSchemaStore } from '../store'
import { DepartmentStore } from '@evertel/department'
import { APIDataDocument, APIDataDepartment, APIDataDocumentSchema } from '@evertel/types'
import { Api } from '@evertel/api'


type DocumentSearchFilter = {
    state?: 'published' | 'archived'
    schemaId?: number | null
    access?: 'owner' | 'any' | 'orphan' | null
    text?: string
    by?: {
        type: 'department' | 'room'
        id: number
    } | null
}
class DocumentSearchController {
    isLoading = false
    documentsCount = 0
    documentIds = []
    documentSchemaIds = []
    documentDepartmentIds = []
    isInfiniteScroll = true
    page = 1
    limit = 100
    //needs to be lowercase to work with bootstrapTable
    order = 'updatedDate desc'
    filter: DocumentSearchFilter = {
        by: null,
        text: '',
        state: 'published'
    }
    //this allows the page number to be restored when loading the search from a url, otherwise it gets reset
    keepPageNextCycle = false

    constructor(
        private documentStore: DocumentStore,
        private documentSchemaStore: DocumentSchemaStore,
        private departmentStore: DepartmentStore,
        private documentACLStore: DocumentACLStore,
        private api: Api
    ) {
        makeAutoObservable(this)
    }

    init({isInfiniteScroll = true}) {
        this.isInfiniteScroll = isInfiniteScroll
        this.resetSearchMeta()
    }

    setPage(pageNumber: number, keep = false) {
        if (!this.keepPageNextCycle) {
            this.page = pageNumber
        }
        this.keepPageNextCycle = keep
    }
    setFilter(filter: DocumentSearchFilter) {
        this.resetSearchMeta()
        this.filter = filter
    }

    setOrder(newOrder) {
        this.resetSearchMeta()
        this.order = newOrder
    }

    setLimit(limit: number) {
        this.resetSearchMeta()
        this.limit = limit
    }

    resetSearchMeta() {
        this.documentsCount = 0
        this.documentIds = []
        this.setPage(1)
    }

    search = async () => {
        this.isLoading = true

        let count = this.documentsCount

        if (this.keepPageNextCycle) this.keepPageNextCycle = false

        // grab the total count of documents
        if (!this.documentsCount) {
            count = await this.fetchDocumentsCount()
        }

        const where = this.prepareSearchWhere()

        // stop from fetching if we already got them all
        if (count > this.documentIds?.length) {
            let documents: APIDataDocument[]

            const params = {
                where,
                order: this.order,
                limit: this.limit,
                skip: this.limit * (this.page - 1),
                include: ['media', 'documentSchema', 'department', {
                    relation: 'ACL',
                    scope: {
                        include: [{
                            relation: 'principal',
                            scope: {
                                fields: ['id', 'firstName', 'lastName', 'email']
                            }
                        }]
                    }
                }]
            }

            if (!this.filter.by?.id) {
                // no 'by' params then search all user's docs
                documents = await this.api.Routes.Document.getSearch(params)

            } else if (this.filter.by.type === 'department') {
                documents = await this.api.Routes.Document.getSearch(params)

            } else if (this.filter.by.type === 'room') {
                documents = await this.api.Routes.Room.getDocumentsSearch(this.filter.by.id, params)

            } else {
                throw new Error(`Invalid 'by' when searching documents`)
            }

            const departments = documents.map(d => d.department)
            const documentIds = documents.map((d) => d.id)
            const acls = documents.map(d => d.ACL)?.flat(1)

            // save the resulting docs in the documentStore
            runInAction(() => {
                this.documentStore.update(documents)
                this.documentACLStore.update(acls)
                this.departmentStore.update(departments)
            })

            // TODO: save the principal Users / Rooms / Departments in respective stores.

            // update observables
            runInAction(() => {
                this.documentsCount = count
                this.documentIds = (this.isInfiniteScroll) ? [...this.documentIds, ...documentIds] : documentIds
            })
        }
        runInAction(() => {
            this.isLoading = false
        })
    }

    async fetchDocumentsCount() {
        const where = this.prepareSearchWhere()

        if (!this.filter?.by?.id) {
            const result = await this.api.Routes.Document.getSearchCount(where)
            return result.count

        } else if (this.filter?.by?.type === 'department') {
            const result = await this.api.Routes.Document.getSearchCount(where)
            return result.count

        } else if (this.filter?.by?.type === 'room') {
            const result = await this.api.Routes.Room.getDocumentsSearchCount(this.filter.by.id, where)
            return result.count

        } else {
            throw new Error(`Invalid 'by' when searching documents`)
        }
    }

    public async fetchDocumentSchemas(): Promise<void> {
        const count = (await this.api.Routes.Document.getSearchDocumentSchemaCount())?.count || 0

        const pages = Math.ceil(count / 100)
        const promises: Promise<APIDataDocumentSchema[]>[] = []

        for (let i = 0; i < pages; i++) {
            promises.push(this.api.Routes.Document.getSearchDocumentSchema({
                include: [{'relation': 'department'}, {'relation': 'iconMedia'}],
                skip: 100 * i,
                limit: 100
            }))
        }

        const fetchedSchemas = await Promise.all(promises)
        const documentSchemas = fetchedSchemas.filter(Boolean).flat()

        const departments = documentSchemas
            .map(({department}) => department as APIDataDepartment)
            .filter(Boolean)

        this.documentSchemaStore.update(documentSchemas)
        this.departmentStore.update(departments)

        const uniqueDepartmentIds = [...new Set(departments.map(d => d.id))]

        runInAction(() => {
            this.documentSchemaIds = documentSchemas.map(ds => ds.id)
            this.documentDepartmentIds = uniqueDepartmentIds
        })
    }

    protected prepareSearchWhere() {
        type Where = {
            departmentId?: number
            documentSchemaId?: number
            accessType?: string
            searchTerm?: string
            isArchived?: boolean
        }

        let where: Where = {
            searchTerm: this.filter.text
        }

        if (this.filter.by?.type === 'department') {
            where = {
                ...where,
                departmentId: this.filter.by.id
            }
        }

        switch (this.filter.state) {
            case 'published':
                where = {
                    ...where,
                    isArchived: false
                }

                break
            case 'archived':
                where = {
                    ...where,
                    isArchived: true
                }

                break
            default:
                throw new Error(`Unknown state: ${this.filter.state} (typeof ${typeof this.filter.state})`)
        }

        if (this.filter.schemaId) {
            where = {
                ...where,
                documentSchemaId: this.filter.schemaId
            }
        }

        if (this.filter.access) {
            where = {
                ...where,
                accessType: this.filter.access
            }
        }

        return where
    }

    get documents(): APIDataDocument[] {
        const docs = this.documentStore.findByIds(this.documentIds)
        return docs.map(d => ({
            ...d,
            ACL: this.documentACLStore.objectsArray.filter(a => a.documentId === d.id),
            department: this.departmentStore.objectsArray.filter(dept => dept.id === d.departmentId)[0]
        }))
    }

    get documentSchemas() {
        return this.documentSchemaStore.findByIds(this.documentSchemaIds)
    }

    get documentDepartments() {
        return this.departmentStore.findByIds(this.documentDepartmentIds)
    }
}

decorate(injectable(), DocumentSearchController)
decorate(inject(DocumentStore), DocumentSearchController, 0)
decorate(inject(DocumentSchemaStore), DocumentSearchController, 1)
decorate(inject(DepartmentStore), DocumentSearchController, 2)
decorate(inject(DocumentACLStore), DocumentSearchController, 3)
decorate(inject(Api), DocumentSearchController, 4)

export { DocumentSearchController, DocumentSearchFilter }
